import { TaskDropdownOption } from '../types';
import apiService from './api';

export interface ConfigurationResponse {
  success: boolean;
  data?: Record<string, TaskDropdownOption[]>;
  message?: string;
}

/**
 * Generic Configuration Service
 *
 * This service provides a centralized way to fetch dropdown configurations
 * from the backend. Any component can request specific configuration keys
 * and receive all data in a single API call.
 *
 * Usage Examples:
 * - Task forms: ['task-priorities', 'task-categories', 'project-roles']
 * - User forms: ['user-roles', 'user-statuses', 'departments']
 * - Project forms: ['project-types', 'project-statuses', 'industries']
 */
class ConfigService {
  private configCache: Map<string, { data: TaskDropdownOption[]; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Fetch multiple configurations in a single API call
   *
   * @param keys Array of configuration keys to fetch
   * @returns Promise with all requested configurations
   *
   * @example
   * const configs = await configService.getBulkConfigurations([
   *   'task-priorities',
   *   'task-categories',
   *   'project-roles'
   * ]);
   *
   * // Usage:
   * setPriorityOptions(configs.priorities);
   * setCategoryOptions(configs.categories);
   * setRoleOptions(configs.roles);
   */
  async getBulkConfigurations(keys: string[]): Promise<ConfigurationResponse> {
    try {
      if (!keys || keys.length === 0) {
        return {
          success: false,
          message: 'Configuration keys are required',
        };
      }

      // Check cache for all keys first
      const cachedResults: Record<string, TaskDropdownOption[]> = {};
      const uncachedKeys: string[] = [];

      keys.forEach(key => {
        const cached = this.configCache.get(key);
        if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
          cachedResults[key] = cached.data;
        } else {
          uncachedKeys.push(key);
        }
      });

      // If all keys are cached, return cached data
      if (uncachedKeys.length === 0) {
        return {
          success: true,
          data: cachedResults,
        };
      }

      // Fetch uncached configurations from API
      const response = await apiService.post('/configurations/dropdowns/bulk', {
        keys: uncachedKeys,
      });

      if (response.data?.success && response.data?.data) {
        // Cache the new data
        Object.entries(response.data.data).forEach(([key, options]) => {
          this.configCache.set(key, {
            data: options as TaskDropdownOption[],
            timestamp: Date.now(),
          });
        });

        // Combine cached and new data
        const allConfigurations = {
          ...cachedResults,
          ...response.data.data,
        };

        return {
          success: true,
          data: allConfigurations,
        };
      }

      return {
        success: false,
        message: 'Failed to fetch configurations',
      };
    } catch (error) {
      console.error('Error fetching bulk configurations:', error);
      return {
        success: false,
        message: 'Failed to fetch configurations',
      };
    }
  }

  /**
   * Get a single configuration by key (with caching)
   *
   * @param key Configuration key
   * @returns Promise with configuration options
   */
  async getConfiguration(key: string): Promise<TaskDropdownOption[]> {
    const result = await this.getBulkConfigurations([key]);
    return result.data?.[key] || [];
  }

  /**
   * Clear cache for specific keys or all cache
   *
   * @param keys Optional array of keys to clear, or undefined to clear all
   */
  clearCache(keys?: string[]): void {
    if (keys) {
      keys.forEach(key => this.configCache.delete(key));
    } else {
      this.configCache.clear();
    }
  }

  /**
   * Convenient method for task-related configurations
   * Returns all task dropdown configurations in a structured format
   */
  async getTaskConfigurations(): Promise<{
    priorities: TaskDropdownOption[];
    categories: TaskDropdownOption[];
    statuses: TaskDropdownOption[];
    progressStatuses: TaskDropdownOption[];
    roles: TaskDropdownOption[];
  }> {
    const result = await this.getBulkConfigurations([
      'task-priorities',
      'task-categories',
      'task-statuses',
      'task-progress-status',
      'project-roles',
    ]);

    return {
      priorities: result.data?.['task-priorities'] || [],
      categories: result.data?.['task-categories'] || [],
      statuses: result.data?.['task-statuses'] || [],
      progressStatuses: result.data?.['task-progress-status'] || [],
      roles: result.data?.['project-roles'] || [],
    };
  }

  /**
   * Convenient method for project-related configurations
   * Example for future use
   */
  async getProjectConfigurations(): Promise<{
    types: TaskDropdownOption[];
    statuses: TaskDropdownOption[];
    industries: TaskDropdownOption[];
  }> {
    const result = await this.getBulkConfigurations([
      'project-types',
      'project-statuses',
      'industries',
    ]);

    return {
      types: result.data?.['project-types'] || [],
      statuses: result.data?.['project-statuses'] || [],
      industries: result.data?.['industries'] || [],
    };
  }

  /**
   * Convenient method for user-related configurations
   * Example for future use
   */
  async getUserConfigurations(): Promise<{
    roles: TaskDropdownOption[];
    statuses: TaskDropdownOption[];
    departments: TaskDropdownOption[];
  }> {
    const result = await this.getBulkConfigurations(['user-roles', 'user-statuses', 'departments']);

    return {
      roles: result.data?.['user-roles'] || [],
      statuses: result.data?.['user-statuses'] || [],
      departments: result.data?.['departments'] || [],
    };
  }
}

export default new ConfigService();
